import React, { useEffect, useState, useCallback, useRef } from 'react';
import { LifeBuoy, Loader, X, Clock, ShieldCheck, AlertCircle, MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, UserCircle, Code2, ArrowUpRight, Paperclip, Image as ImageIcon, Video as VideoIcon, FileText } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import PageHeader from '../../components/PageHeader';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import { PAGE } from '../../components/crm/ui';
import { safeHttpUrl } from '../../utils/safeUrl';

const STATUS_OPTIONS = ['open', 'pending', 'resolved', 'closed'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const CATEGORY_OPTIONS = ['general', 'billing', 'technical', 'refund', 'account', 'other'];

const STATUS_BADGE = {
  open: 'sf-badge-error',
  pending: 'sf-badge-warning',
  resolved: 'sf-badge-success',
  closed: 'sf-badge-neutral',
};

const PRIORITY_BADGE = {
  urgent: 'sf-badge-error',
  high: 'sf-badge-warning',
  medium: 'sf-badge-manager',
  low: 'sf-badge-neutral',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString();
}

const PAGE_LIMIT = 20;

export default function SupportTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ subject: '', description: '', priority: 'medium', category: 'general', requesterEmail: '', requesterName: '' });
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [planError, setPlanError] = useState(null);
  const [escalating, setEscalating] = useState(false);
  const [escalateAssignee, setEscalateAssignee] = useState('');
  const socketRef = useRef(null);

  const load = useCallback(async (pg = page) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: PAGE_LIMIT };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const res = await api.get('/support/tickets', { params });
      setTickets(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (err) {
      // A plan-gate 403 was previously reported the same as any other load
      // failure — a toast plus a permanently-empty ticket list, with nothing
      // telling the user *why* tickets never appear.
      if (err.response?.status === 403) {
        setPlanError(err.response.data?.message || err.response.data?.error || 'This feature is not available on your current plan.');
      } else {
        alertService.error('Error', err.response?.data?.error || 'Failed to load tickets.');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, page]);

  useEffect(() => { load(); }, [load]);

  // Load team members for assignee picker
  useEffect(() => {
    api.get('/admin/team').then(res => {
      setTeamMembers(res.data?.members || res.data?.data || []);
    }).catch(() => {});
  }, []);

  // Real-time: subscribe to inbox namespace so AI-created tickets appear instantly
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io('/inbox', {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('inbox:new_message', () => {
      // A new message may have created a ticket — refresh the list quietly
      load();
    });

    socket.on('ticket:created', () => {
      load();
    });

    return () => { socket.disconnect(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter]);

  const updateTicket = async (id, updates) => {
    setSaving(true);
    try {
      const res = await api.put(`/support/tickets/${id}`, updates);
      const updated = res.data.data;
      setTickets(prev => prev.map(t => (t._id === id ? updated : t)));
      setSelected(updated);
      alertService.success('Saved', 'Ticket updated.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to update ticket.');
    } finally {
      setSaving(false);
    }
  };

  const escalateToDev = async (id) => {
    setEscalating(true);
    try {
      const res = await api.post(`/support/tickets/${id}/escalate-to-dev`, { assignedTo: escalateAssignee || undefined });
      const updatedTicket = res.data.data.ticket;
      setTickets(prev => prev.map(t => (t._id === id ? updatedTicket : t)));
      setSelected(updatedTicket);
      alertService.success('Escalated', 'A development task was created from this ticket.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to escalate to Development.');
    } finally {
      setEscalating(false);
    }
  };

  const deleteTicket = async (id) => {
    if (!(await alertService.confirm('Delete?', 'Delete this ticket? This cannot be undone.', 'danger', 'Delete'))) return;
    setDeleting(true);
    try {
      await api.delete(`/support/tickets/${id}`);
      setTickets(prev => prev.filter(t => t._id !== id));
      setTotal(t => t - 1);
      setSelected(null);
      alertService.success('Deleted', 'Ticket removed.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to delete ticket.');
    } finally {
      setDeleting(false);
    }
  };

  const createTicket = async () => {
    if (!createForm.subject || !createForm.description) {
      alertService.error('Missing fields', 'Subject and description are required.');
      return;
    }
    setSaving(true);
    try {
      // multipart/form-data whenever there's at least one attachment — a
      // plain JSON post has no way to carry the actual file bytes. Sent as
      // plain JSON otherwise so the request stays identical to before for
      // the common no-attachment case.
      if (attachmentFiles.length > 0) {
        const formData = new FormData();
        Object.entries(createForm).forEach(([key, value]) => formData.append(key, value ?? ''));
        attachmentFiles.forEach((file) => formData.append('attachments', file));
        await api.post('/support/tickets', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/support/tickets', createForm);
      }
      alertService.success('Created', 'Ticket created.');
      setShowCreate(false);
      setCreateForm({ subject: '', description: '', priority: 'medium', category: 'general', requesterEmail: '', requesterName: '' });
      setAttachmentFiles([]);
      load();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to create ticket.');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const goToPage = (pg) => {
    const clamped = Math.max(1, Math.min(pg, totalPages));
    setPage(clamped);
    load(clamped);
  };

  if (planError) {
    return (
      <FeatureLockedPage
        title="Support Tickets"
        subtitle="Track and resolve customer support conversations escalated from the AI agent."
        icon={LifeBuoy}
        featureName="Support Tickets"
        description={planError}
      />
    );
  }

  return (
    <div className={PAGE}>
      <PageHeader
        title="Support Tickets"
        subtitle="Issues logged by the AI support agent and your team, in one queue."
        icon={LifeBuoy}
        actions={
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 bg-[#0176D3] hover:bg-[#014486] text-white px-4 py-2 rounded text-[13px] font-medium">
            <Plus size={16} /> New Ticket
          </button>
        }
      />

      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] text-[#080707] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={priorityFilter}
          onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] text-[#080707] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
        >
          <option value="">All priorities</option>
          {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="ml-auto text-xs text-[#706E6B]">{total} ticket{total !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader className="animate-spin text-[#0176D3]" size={28} /></div>
      ) : tickets.length === 0 ? (
        <div className="sf-card py-24 text-center border-dashed border-2 opacity-50">
          <LifeBuoy className="mx-auto mb-4 opacity-20" size={48} />
          <p className="text-sm font-bold text-[#706E6B] uppercase ">No tickets found</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {tickets.map(ticket => (
              <button
                key={ticket._id}
                onClick={() => setSelected(ticket)}
                className="sf-card w-full text-left p-4 flex items-center gap-4 hover:border-[#0176D3] transition-all"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-[#706E6B]">{ticket.ticketNumber}</span>
                    <span className={cn('sf-badge', STATUS_BADGE[ticket.status])}>{ticket.status}</span>
                    <span className={cn('sf-badge', PRIORITY_BADGE[ticket.priority])}>{ticket.priority}</span>
                    {ticket.createdBy === 'ai' && (
                      <span className="sf-badge sf-badge-manager flex items-center gap-1"><ShieldCheck size={11} /> AI-created</span>
                    )}
                  </div>
                  <h4 className="text-[14px] font-bold text-[#080707] truncate">{ticket.subject}</h4>
                  <p className="text-xs text-[#706E6B] truncate mt-0.5">{ticket.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] text-[#706E6B] flex items-center gap-1 justify-end"><Clock size={11} /> {fmtDate(ticket.createdAt)}</p>
                  {ticket.assignedTo && (
                    <p className="text-[11px] text-[#0176D3] font-semibold mt-1">{ticket.assignedTo.firstName} {ticket.assignedTo.lastName}</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="p-1.5 rounded border border-[#D8DDE6] disabled:opacity-30 hover:bg-[#F3F2F2] text-[#706E6B]"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-xs text-[#706E6B]">Page {page} of {totalPages}</span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="p-1.5 rounded border border-[#D8DDE6] disabled:opacity-30 hover:bg-[#F3F2F2] text-[#706E6B]"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail slide-over */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6 space-y-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-[#080707] flex items-center gap-2">
                <MessageSquare size={18} className="text-[#0176D3]" /> {selected.ticketNumber}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => deleteTicket(selected._id)}
                  disabled={deleting}
                  title="Delete ticket"
                  className="p-1.5 text-[#BA0517] hover:bg-[#FDECEA] rounded disabled:opacity-50"
                >
                  <Trash2 size={16} />
                </button>
                <button onClick={() => setSelected(null)} className="text-[#706E6B] hover:text-[#080707]"><X size={20} /></button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-[#080707]">{selected.subject}</h3>
              <p className="text-sm text-[#706E6B] mt-2 whitespace-pre-wrap">{selected.description}</p>
            </div>

            {selected.attachments?.length > 0 && (
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-2 flex items-center gap-1">
                  <Paperclip size={12} /> Attachments ({selected.attachments.length})
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {selected.attachments.map((a, i) => (
                    <a
                      key={i}
                      href={safeHttpUrl(a.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="block border border-[#D8DDE6] rounded-lg overflow-hidden hover:border-[#0176D3] transition-colors"
                      title={a.filename}
                    >
                      {a.type === 'image' ? (
                        <img src={a.url} alt={a.filename} className="w-full h-20 object-cover" />
                      ) : a.type === 'video' ? (
                        <div className="w-full h-20 bg-[#080707] flex items-center justify-center">
                          <VideoIcon size={20} className="text-white" />
                        </div>
                      ) : (
                        <div className="w-full h-20 bg-[#F3F2F2] flex items-center justify-center">
                          <FileText size={20} className="text-[#706E6B]" />
                        </div>
                      )}
                      <p className="text-[10px] text-[#706E6B] truncate px-1.5 py-1">{a.filename}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {selected.escalationReason && (
              <div className="sf-card p-3 bg-[#FFF4E5] border-[#FFCD94] flex gap-2">
                <AlertCircle size={16} className="text-[#B35F00] shrink-0 mt-0.5" />
                <p className="text-xs text-[#B35F00]">{selected.escalationReason}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Status</label>
                <select
                  value={selected.status}
                  disabled={saving}
                  onChange={e => updateTicket(selected._id, { status: e.target.value })}
                  className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]"
                >
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Priority</label>
                <select
                  value={selected.priority}
                  disabled={saving}
                  onChange={e => updateTicket(selected._id, { priority: e.target.value })}
                  className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]"
                >
                  {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            {/* Assignee picker */}
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5 flex items-center gap-1">
                <UserCircle size={12} /> Assigned to
              </label>
              <select
                value={selected.assignedTo?._id || selected.assignedTo || ''}
                disabled={saving}
                onChange={e => updateTicket(selected._id, { assignedTo: e.target.value || null })}
                className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]"
              >
                <option value="">Unassigned</option>
                {teamMembers.map(m => (
                  <option key={m._id} value={m._id}>
                    {m.firstName} {m.lastName} ({m.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Support -> Development handoff */}
            <div className="pt-3 border-t border-[#D8DDE6]">
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5 flex items-center gap-1">
                <Code2 size={12} /> Development
              </label>
              {selected.devTaskId ? (
                <span className="sf-badge sf-badge-manager flex items-center gap-1 w-fit">
                  <ArrowUpRight size={11} /> Escalated to Development
                </span>
              ) : (
                <div className="space-y-2">
                  <select
                    value={escalateAssignee}
                    disabled={escalating}
                    onChange={e => setEscalateAssignee(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]"
                  >
                    <option value="">Unassigned developer</option>
                    {teamMembers.map(m => (
                      <option key={m._id} value={m.email}>{m.firstName} {m.lastName} ({m.email})</option>
                    ))}
                  </select>
                  <button
                    onClick={() => escalateToDev(selected._id)}
                    disabled={escalating}
                    className="w-full flex items-center justify-center gap-1.5 bg-[#080707] hover:bg-[#292828] disabled:opacity-60 text-white py-2 rounded text-sm font-medium"
                  >
                    <Code2 size={14} /> {escalating ? 'Escalating…' : 'Escalate to Development'}
                  </button>
                </div>
              )}
            </div>

            <div className="text-xs text-[#706E6B] space-y-1 pt-3 border-t border-[#D8DDE6]">
              {selected.requesterName && <p><strong>Requester:</strong> {selected.requesterName}</p>}
              {selected.requesterEmail && <p><strong>Email:</strong> {selected.requesterEmail}</p>}
              <p><strong>Created:</strong> {fmtDate(selected.createdAt)}</p>
              {selected.resolvedAt && <p><strong>Resolved:</strong> {fmtDate(selected.resolvedAt)}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={() => { setShowCreate(false); setAttachmentFiles([]); }}>
          <div className="bg-white w-full max-w-md p-4 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto rounded-t-lg sm:rounded-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-[#2E2E2E]">New Support Ticket</h2>
              <button onClick={() => { setShowCreate(false); setAttachmentFiles([]); }} className="text-[#706E6B] hover:text-[#080707]"><X size={20} /></button>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Subject</label>
              <input type="text" placeholder="Brief summary of the issue" value={createForm.subject} onChange={e => setCreateForm(f => ({ ...f, subject: e.target.value }))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Detailed Description</label>
              <textarea
                placeholder={'What happened, and what did you expect instead?\nSteps to reproduce (if applicable):\n1. \n2. \n3. '}
                value={createForm.description}
                onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] h-32 leading-relaxed"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select value={createForm.priority} onChange={e => setCreateForm(f => ({ ...f, priority: e.target.value }))} className="px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={createForm.category} onChange={e => setCreateForm(f => ({ ...f, category: e.target.value }))} className="px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input type="text" placeholder="Requester name" value={createForm.requesterName} onChange={e => setCreateForm(f => ({ ...f, requesterName: e.target.value }))} className="px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              <input type="email" placeholder="Requester email" value={createForm.requesterEmail} onChange={e => setCreateForm(f => ({ ...f, requesterEmail: e.target.value }))} className="px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5 flex items-center gap-1">
                <Paperclip size={12} /> Screenshots or screen recording (optional)
              </label>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#D8DDE6] hover:border-[#0176D3] rounded-lg px-4 py-4 cursor-pointer text-[12px] text-[#706E6B] transition-colors">
                <Paperclip size={14} />
                {attachmentFiles.length > 0 ? `${attachmentFiles.length} file${attachmentFiles.length > 1 ? 's' : ''} selected` : 'Click to attach images or a short video (up to 5 files, 25MB each)'}
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={e => setAttachmentFiles(Array.from(e.target.files || []).slice(0, 5))}
                />
              </label>
              {attachmentFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {attachmentFiles.map((f, i) => (
                    <span key={i} className="flex items-center gap-1 bg-[#F3F2F2] border border-[#D8DDE6] rounded-full px-2 py-1 text-[11px] text-[#080707]">
                      {f.type.startsWith('video/') ? <VideoIcon size={11} /> : <ImageIcon size={11} />}
                      {f.name}
                      <button onClick={() => setAttachmentFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-[#706E6B] hover:text-[#BA0517]"><X size={11} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button onClick={createTicket} disabled={saving} className="w-full bg-[#0176D3] hover:bg-[#014486] disabled:opacity-60 text-white py-2.5 rounded text-sm font-medium">
              {saving ? 'Creating…' : 'Create Ticket'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
